import React, {
  useCallback, useMemo, useState, useId,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { actions as modulesActions } from '@/reducers/modules';
import { COLORS } from '@gc-npm/utils.enums.colors';
import sendGaComponentEvent from '@gc-npm/utils.metrics.send-ga-component-event';
import CustomLink from '@gc-npm/components.custom-link';
import IconGlyph from '@gc-npm/components.icon-glyph';
import Label, { LABEL_TYPES } from '@gc-npm/components.cms-block.cms-label';
import CmsSwitch, {
  SWITCH_TYPE,
} from '@components/cms-switch';
import Price, {
  PRICE_DECORATION,
  PRICE_FONT_WEIGHT,
  PRICE_SIZE,
} from '@gc-npm/components.price';
import Select from '@/components/select';
import TextInfo, {
  TEXT_COLOR,
  TEXT_FONT_WEIGHT,
  TEXT_SIZE,
} from '@gc-npm/components.text';

import ShoppingCartFooterNew from '@/components/ShoppingCart/ShoppingCartFooterNew';
import RadioButton from '@/components/radio-button';
import {
  API_STATUS,
  SCREEN_TYPES,
  SHOPPING_CART_FOOTER_LAYOUTS,
} from '@/utils/constants';

import MobileLines from './MobileLines';

const CARD_NAME = 'configCard';
const CATEGORY_CONECTIVIDAD = 'conectividad';
const SUBCATEGORY_CONEXION = 'conexion';
const LAYOUT_SIZE_ICON = { width: 40, height: 40 };

const normalizeText = (text) => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

const hasValidSelectLabel = (value) => {
  if (typeof value !== 'string') return false;
  const normalizedValue = value.trim();
  return normalizedValue !== ''
    && normalizedValue !== '0'
    && normalizedValue !== 'undefined';
};

const getSelectOptionLabel = (option) => {
  if (hasValidSelectLabel(option?.label)) return option.label;
  if (hasValidSelectLabel(option?.labelRecomendador)) {
    return option.labelRecomendador;
  }
  return 'Selecciona una opción';
};

const makeTaggingInfo = (componentContent, overrides = {}) => ({
  componentContent,
  componentName: CARD_NAME,
  componentVariation: 'All',
  eventType: 'click',
  ...overrides,
});

export const Card = ({
  data, cmsHandler, fake, screenType,
}) => {
  const dispatch = useDispatch();
  const cardId = useId();
  const titleId = `config-card-title-${cardId}`;
  const controlId = `config-card-control-${cardId}`;
  const {
    id,
    title,
    titleMultiBundle,
    text2,
    prices,
    selected,
    iconList,
    price,
    hired,
    hiredMultiBundle,
    promoPrice,
    promoLabel,
    promoDuration,
    promotions,
    linkModal,
    logic,
    someCardPromoted,
    someCardHired,
    category,
    subcategory,
    recomendedLabel,
    mobileLines,
  } = data;
  const productType = useSelector((state) => state.client.productType);
  const [activeSelectId, setActiveSelectId] = useState(null);
  const isConexion = category === CATEGORY_CONECTIVIDAD && subcategory === SUBCATEGORY_CONEXION;
  const canDesSelect = useMemo(() => !(isConexion && selected), [isConexion, selected]);
  const statusModulos = useSelector((state) => state.modulos?.statusModulos);
  const statusSelection = useSelector(
    (state) => state.modulos?.statusSelection,
  );
  const apiReady = useMemo(() =>
    statusModulos === API_STATUS.OK && statusSelection === API_STATUS.OK,
  [statusModulos, statusSelection]);

  const baseTaggingInfo = useMemo(() => ({
    componentProduct: logic?.alias || title?.title,
    componentVariation: promoPrice?.price ? 'promoted' : 'All',
  }), [logic?.alias, title?.title, promoPrice?.price]);

  const taggingInfoForModal = useMemo(() => ({
    componentContent: linkModal?.title || '',
    componentName: CARD_NAME,
    ...baseTaggingInfo,
  }), [linkModal?.title, baseTaggingInfo]);

  const taggingInfoForSwitch = useMemo(() => ({
    ...makeTaggingInfo('switch', baseTaggingInfo),
  }), [baseTaggingInfo]);

  const taggingInfoForRadio = useMemo(() => ({
    ...makeTaggingInfo('radio', baseTaggingInfo),
  }), [baseTaggingInfo]);

  const finalPromoPrice = useMemo(() => {
    const contractPrice = prices?.contractPrices?.price;
    const configuredPromoPrice = promoPrice?.price;
    const hasConfiguredPromo = configuredPromoPrice != null
      && configuredPromoPrice !== ''
      && contractPrice != null
      && Number(configuredPromoPrice) !== Number(contractPrice);

    if (hasConfiguredPromo) return configuredPromoPrice;

    const promotionContractPrice = promotions?.find((promotion) => {
      const promotionPrice = promotion?.prices?.contractPrices?.price;
      return promotionPrice != null
        && promotionPrice !== ''
        && contractPrice != null
        && Number(promotionPrice) !== Number(contractPrice);
    })?.prices?.contractPrices?.price;

    if (promotionContractPrice != null) return promotionContractPrice;

    return null;
  }, [promotions, promoPrice?.price, prices?.contractPrices?.price]);
  const hasFinalPromoPrice = finalPromoPrice != null;

  const validInputSelects = useMemo(() => {
    return (logic?.inputSelects || []).filter(
      (select) => Array.isArray(select.options) && select.options.length > 0,
    );
  }, [logic?.inputSelects]);

  // Calculamos los valores iniciales: usamos la opción preseleccionada o el primer valor.
  const initialSelectedOptions = useMemo(() => {
    return validInputSelects.map((select) => {
      const orderoptions = select.options.sort((a, b) => a.order - b.order);
      const preselected = orderoptions.find((opt) => opt.selected);
      return preselected ? preselected.value : orderoptions[0]?.value || '';
    });
  }, [validInputSelects]);

  // Estado para almacenar los valores actuales de cada select
  // (la longitud es la de los selects válidos)
  const [selectedOptions, setSelectedOptions] = useState(
    initialSelectedOptions,
  );

  const selectModule = useCallback(({ taggingInfo, moduleId = id, qty = 1 }) => {
    if (!apiReady) return;
    if (taggingInfo) sendGaComponentEvent(taggingInfo);
    dispatch(
      modulesActions.selectModules({
        ids: [{ id: moduleId, qty }],
        category: normalizeText(category),
        subcategory: normalizeText(subcategory),
      }),
    );
  }, [apiReady, dispatch, id, category, subcategory]);

  const handleSelectChange = useCallback(
    (newOptionId, index, label) => {
      setSelectedOptions((prevOptions) => {
        if (prevOptions[index] === newOptionId) return prevOptions;
        const updatedOptions = [...prevOptions];
        updatedOptions[index] = newOptionId;
        const finalId = [id.split('_')[0], ...updatedOptions].join('_');

        let taggingInfoSelect = { ...taggingInfoForSwitch };
        if (label) {
          const productName = titleMultiBundle?.text
            || (title && (logic?.alias || title.text).trim().split(/\s+/)[0])
            || '';
          if (productName) {
            taggingInfoSelect = {
              ...taggingInfoSelect,
              componentContent: `${productName}-${label}-desplegable`,
              componentProduct: productName,
            };
          }
        }

        sendGaComponentEvent(taggingInfoSelect);
        dispatch(
          modulesActions.selectModules({
            ids: [{ id: finalId, qty: 1 }],
            category: normalizeText(category),
            subcategory: normalizeText(subcategory),
          }),
        );
        return updatedOptions;
      });
    },
    [dispatch, id, taggingInfoForSwitch, category, subcategory],
  );

  const onClickHandler = useCallback(
    (taggingInfoFromModal, noTaggSwitch) => {
      if (!apiReady) return;
      if (!noTaggSwitch) {
        const suffix = selected ? 'quitado' : 'añadido';
        const taggingInfo = taggingInfoFromModal
          ? { ...taggingInfoForSwitch, ...taggingInfoFromModal, componentContent: `${taggingInfoForSwitch.componentContent}-${suffix}` }
          : { ...taggingInfoForSwitch, componentContent: `${taggingInfoForSwitch.componentContent}-${suffix}` };
        selectModule({ taggingInfo });
        return;
      }
      selectModule({});
    },
    [apiReady, selected, taggingInfoForSwitch, selectModule],
  );

  const onRadioClickHandler = useCallback(() => {
    selectModule({ taggingInfo: taggingInfoForRadio });
  }, [selectModule, taggingInfoForRadio]);

  // getOnChangeHandler devuelve una función estable para cada select.
  const getOnChangeHandler = useCallback(
    (index) => (selectedValue, param, label) => {
      handleSelectChange(selectedValue, index, label);
    },
    [handleSelectChange],
  );

  const [focusedSelectIndices, setFocusedSelectIndices] = useState(new Set());

  const markSelectFocused = useCallback((idx) => {
    setFocusedSelectIndices((prev) => {
      if (prev.has(idx)) return prev;
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
  }, []);

  const sizeIcon = screenType === SCREEN_TYPES.MOBILE ? 28 : 30;
  const textButtonModal = isConexion ? 'Elegir' : 'Añadir';
  const activeLabel = hired || hiredMultiBundle || recomendedLabel;
  const shoppingCartFooterProps = useMemo(() => ({
    price: prices?.contractPrices?.price,
    onClick: onClickHandler,
    buttonText: `${selected ? 'Quitar' : textButtonModal}`,
    titlePrice: '',
    screenType,
    promoDuration,
    promoPriceAlt: promoPrice,
    finalPromoPrice,
    isConexion,
    selected,
    taggingInfo: taggingInfoForModal,
    quotePromo: 'mes',
  }), [prices?.contractPrices?.price, onClickHandler, selected, textButtonModal,
    screenType, promoDuration, promoPrice, finalPromoPrice, isConexion,
    taggingInfoForModal]);
  return (
    <>
      <div
        onClick={(event) => {
          if (event.target.closest('a, button, input, select, textarea, label, [role="button"], [role="switch"], [role="radio"]')) return;
          if (canDesSelect && !fake) {
            onClickHandler();
          }
        }}
        className={`Card ${!fake && selected ? 'selectedBorder' : ''} ${fake ? 'skeleton' : ''}`}
        role="group"
        aria-labelledby={titleId}
      >
        <div className="Card_content">
          <div className="Card_content-info">
            <div className="Card_content-info__header">
              <div className="Card_content-info__header--icons">
                {iconList
                  && iconList['cms-atom-icon-1']
                  && Array.isArray(iconList['cms-atom-icon-1'])
                  && iconList['cms-atom-icon-1'].map((icon, i) => (
                    <div key={i}>
                      <IconGlyph
                        {...icon}
                        width={sizeIcon}
                        height={sizeIcon}
                        circleLayout={6}
                        backgroundColor={TEXT_COLOR.GREY1}
                        cmsHandler={cmsHandler}
                        layoutSize={LAYOUT_SIZE_ICON}
                      />
                    </div>
                  ))}
              </div>
              <div
                className={`${someCardHired ? 'Card_content-info__header--label' : ''}`}
              >
                {activeLabel && (
                  <Label
                    {...activeLabel}
                    type={LABEL_TYPES.SUCCESS}
                    cmsHandler={cmsHandler}
                  />
                )}
              </div>
            </div>
            <div className="Card_content-info__texts">
              <div className="Card_content-info__texts--title" id={titleId}>
                {(titleMultiBundle || title) && (
                  <TextInfo
                    {...(titleMultiBundle || title)}
                    color={TEXT_COLOR.MOVISTAR_BLACK}
                    fontType={TEXT_FONT_WEIGHT.BOLD}
                    size={TEXT_SIZE.T7}
                    cmsHandler={cmsHandler}
                  />
                )}
              </div>
              {text2 && productType !== '2' && (
                <div className="Card_content-info__texts--description">
                  <TextInfo
                    {...text2}
                    color={TEXT_COLOR.GREY5}
                    fontType={TEXT_FONT_WEIGHT.REGULAR}
                    size={TEXT_SIZE.BODY1}
                    cmsHandler={cmsHandler}
                  />
                </div>
              )}
              <div
                className={`${someCardPromoted ? 'Card_content-info__texts--promo' : ''}`}
              >
                {promoLabel && promoDuration &&(
                  <>
                    <Label
                      {...promoLabel}
                      type={LABEL_TYPES.PROMOCION}
                      cmsHandler={cmsHandler}
                      className="promoLabel"
                      text={promoLabel.text}
                    />
                    <TextInfo
                      text={promoDuration}
                      color={TEXT_COLOR.RED_DARK}
                      fontType={TEXT_FONT_WEIGHT.REGULAR}
                      size={TEXT_SIZE.CAPTION}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="Card_content-action">
            <div className="Card_content-action__price">
              <Price
                {...price}
                price={prices.contractPrices?.price}
                color={hasFinalPromoPrice ? TEXT_COLOR.GREY4 : TEXT_COLOR.MOVISTAR_BLACK}
                decoration={hasFinalPromoPrice && PRICE_DECORATION.LINE_THROUGH}
                quote={!hasFinalPromoPrice && 'mes' }
                currency="€"
                fontType={
                  hasFinalPromoPrice
                    ? PRICE_FONT_WEIGHT.REGULAR
                    : PRICE_FONT_WEIGHT.BOLD
                }
                fontTypeUnity={PRICE_FONT_WEIGHT.REGULAR}
                size={PRICE_SIZE.XS}
                cmsHandler={cmsHandler}
              />
              {hasFinalPromoPrice && (
                <Price
                  {...promoPrice}
                  price={finalPromoPrice}
                  color={TEXT_COLOR.RED_DARK}
                  quote="mes"
                  currency="€"
                  fontType={PRICE_FONT_WEIGHT.BOLD}
                  fontTypeUnity={PRICE_FONT_WEIGHT.REGULAR}
                  size={PRICE_SIZE.XS}
                  cmsHandler={cmsHandler}
                />
              )}
            </div>
            <div className="Card_content-action__status">
              <div
                className="onclickInfo"
                role="presentation"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {linkModal && (
                  <CustomLink
                    {...linkModal}
                    buttonParent={false}
                    additionalContent={(
                      <ShoppingCartFooterNew
                        layoutType={
                          screenType === SCREEN_TYPES.MOBILE
                            ? SHOPPING_CART_FOOTER_LAYOUTS.MODAL_FICHA_MOBILE
                            : SHOPPING_CART_FOOTER_LAYOUTS.MODAL_FICHA_DESKTOP
                        }
                        {...shoppingCartFooterProps}
                      />
                    )}
                    cmsHandler={cmsHandler}
                    taggingInfo={taggingInfoForModal}
                  >
                    <TextInfo
                      text={linkModal?.title}
                      color={TEXT_COLOR.MOVISTAR_BLUE}
                      fontType={TEXT_FONT_WEIGHT.REGULAR}
                      size={TEXT_SIZE.CAPTION3}
                      cmsHandler={cmsHandler}
                      className="hover"
                    />
                  </CustomLink>
                )}
              </div>
              {isConexion ? (
                <div>
                  <RadioButton
                    id={controlId}
                    name="conexion"
                    value={id}
                    checked={selected}
                    onChange={() => onClickHandler()}
                    disabled={fake}
                    formProps={{ 'aria-labelledby': titleId }}
                  />
                </div>
              ) : (
                canDesSelect && (
                  <CmsSwitch
                    cmsSwitchProps={{
                      onChange: onClickHandler,
                      selectedState: selected,
                      id: controlId,
                      inputName: `modulo-${id}`,
                      ariaLabelledby: titleId,
                      disabled: fake,
                      type: SWITCH_TYPE.PRIMARY,
                      tagginInfo: { debug: true },
                    }}
                  />
                )
              )}
            </div>
            {validInputSelects.length > 0 && (
              <div
                className="Card_content-action__selectors"
                role="presentation"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {validInputSelects.map((selectInput, idx) => {
                  const orderoptions = [...selectInput.options]
                    .sort((a, b) => a.order - b.order)
                    .map((option) => ({
                      ...option,
                      label: getSelectOptionLabel(option),
                    }));
                  const currentValue = selectedOptions[idx] || orderoptions[0]?.value || '';
                  const currentOption = orderoptions.find((opt) => opt.value === currentValue)
                    || orderoptions[0]
                    || {};

                  const isFirstOption = currentOption.value === '0';
                  const defaultName = hasValidSelectLabel(selectInput?.defaultName)
                    ? selectInput.defaultName
                    : '';
                  const shouldShowDefaultName = defaultName
                    && isFirstOption
                    && !focusedSelectIndices.has(idx);
                  return (
                    <div
                      key={`${selectInput.name || 'select'}-${idx}`}
                      onClick={() => markSelectFocused(idx)}
                      onFocus={() => markSelectFocused(idx)}
                      role="presentation"
                    >
                      <Select
                        name={`${currentOption.id}${currentOption.label}`}
                        ariaLabel={selectInput.name ? `Plan ${selectInput.name}` : defaultName || `Opción ${idx + 1}`}
                        activeSelectId={activeSelectId}
                        setActiveSelectId={setActiveSelectId}
                        instanceId={`select-${currentOption.id}-${idx}`}
                        options={orderoptions}
                      defaultValue={
                        shouldShowDefaultName
                          ? defaultName
                          : currentOption.value
                      }
                      value={
                        shouldShowDefaultName
                          ? {
                            value: '',
                            label: defaultName,
                            isDisabled: true, // esto asegura que no se pueda seleccionar si llega a entrar en el menú
                          }
                          : orderoptions.find(
                            (opt) => opt.value === currentValue,
                          )
                      }
                      onChange={getOnChangeHandler(idx)}
                      required={false}
                      isSearchable={false}
                      taggingInfo={{
                        componentName: 'Select',
                        selectIndex: idx,
                      }}
                      size="small"
                    />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      </div>
      <style jsx>
        {`
          .Card {
            width: 100%;
            display: flex;
            padding: 1rem;
            border: 1.6px solid ${COLORS.grey200};
            border-radius: 0.5rem;
            flex-direction: column;
            justify-content: flex-start;
            gap: 1rem;

            &_content {
              height: 100%;
              display: flex;
              flex-direction: column;
              gap: 0.5rem;

              &-info {
                display: flex;
                flex-direction: column;
                gap: 0.2rem;

                &__header {
                  &--icons {
                    display: flex;
                    justify-content: flex-start;
                    gap: 0.2rem;
                    align-items: center;
                  }
                  &--label {
                    min-height: 22px;
                  }
                }

                &__texts {
                  display: flex;
                  flex-direction: column;
                  gap: 0.5rem;
                  justify-content: space-between;

                  &--title {
                    display: flex;
                    align-items: flex-start;
                    line-height: 1.2;
                    width: -webkit-fill-available;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    min-height: 50px;
                  }

                  &--promo {
                    min-height: 43px;
                    display: flex;
                    flex-direction: column;
                    gap: 0.2rem;
                  }
                }
              }

              &-action {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;

                &__price {
                  display: flex;
                  flex-direction: row;
                  gap: 0.5rem;
                }

                &__status {
                  display: flex;
                  flex-direction: row;
                  gap: 0.5rem;
                  justify-content: space-between;
                  align-items: flex-end;

                  .onclickInfo {
                    z-index: 20;
                    :global(.hover:hover) {
                      text-decoration: underline;
                    }
                  }
                }

                &__selectors {
                  min-height: 78px;
                  display: flex;
                  flex-direction: column;
                  gap: 0.5rem;
                }
              }
            }
          }

          .Card :hover {
            cursor: ${canDesSelect ? 'pointer' : 'default'};
            background-color: ${canDesSelect
          ? COLORS.grey100
          : COLORS.white};
          }

          .selectedBorder {
            border: 1.8px solid ${COLORS.movistarBlue};
          }

          @media screen and (max-width: 768px) {
            .Card {
              padding: 0.75rem;

              &_content {
                justify-content: space-between;
                &-action {
                  &__price {
                    /* mantengo el mismo display: flex si lo necesitas aquí */
                  }
                  &__selectors {
                    min-height: unset;
                  }
                }
              }
            }
          }

          @media screen and (max-width: 420px) {
            .Card {
              padding: 0.75rem;

              &_content {
              &-action {
                &__price {
                  display: flex;
                  flex-direction: row;
                  gap: 0.5rem;
                  }
                  &__selectors {
                    /* vacíos a propósito */
                  }
                }
              }
            }
          }
        `}
      </style>
    </>
  );
};

Card.propTypes = {
  className: PropTypes.string,
  data: PropTypes.object.isRequired,
};

Card.defaultProps = {
  className: '',
};

export default Card;
